import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { queryKeys } from './query-keys';

type Params = {
  /** The name of the chart to get the values for */
  chart: string;
  /** The repository URL or registry ID */
  repo?: string;
  /** The version of the chart to get the values for */
  version?: string;
  /** The registry ID */
  registryId?: number;
  /** The environment ID */
  environmentId?: EnvironmentId;
};

export function useHelmChartValues(params: Params, isLatestVersion = false) {
  const hasValidRepoUrl = !!params.repo;
  const hasValidRegistryParams =
    !!params.registryId && !!params.environmentId && !!params.version;
  const hasRegistryAndRepo = hasValidRepoUrl && hasValidRegistryParams;
  // A self-contained "oci://host/path" chart reference carries its own source
  // (e.g. Portainer addon charts), so no repo or registry is needed.
  const hasSelfContainedOciChart = !!params.chart.startsWith('oci://');

  return useQuery({
    queryKey: queryKeys.chartValues(
      params.repo || String(params.registryId),
      params.chart,
      isLatestVersion ? 'latest' : params.version || ''
    ),
    queryFn: () =>
      getHelmChartValues(
        // send only the registryId if the repo and registryId are both set, to avoid a conflicts
        hasRegistryAndRepo ? { ...params, repo: undefined } : params
      ),
    enabled:
      !!params.chart &&
      (hasValidRepoUrl || hasValidRegistryParams || hasSelfContainedOciChart),
    select: (data) => ({
      values: data,
    }),
    retry: 2,
    staleTime: 60 * 1000 * 20, // 60 minutes, because values are not expected to change often
    ...withError('Unable to get Helm chart values'),
  });
}

async function getHelmChartValues(params: Params) {
  try {
    const response = await axios.get<string>(`/templates/helm/values`, {
      params,
    });
    return response.data;
  } catch (err) {
    throw parseAxiosError(err, 'Unable to get Helm chart values');
  }
}
